import { formatDate } from '@smarttransit/common'
import moment from 'moment-timezone'
import { initBreadcrumbs, isAuthorized, addAlert, isOnlyInvestor as isOnlyInvestorFn } from '@/utilities/helpers'

import {
  getEvent,
  createEvent,
  updateEvent,
  archiveEvent,
  unarchiveEvent,
  getEventCapacityStatus
} from '@/services/transportation-profile-events-service'

import { findTransportationProfiles } from '@/services/transportation-profiles-service'

export default {
  name: 'routes-events-event',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      currentEvent: {
        name: '',
        eventDate: null,
        flatRateFee: null,
        returnFlatRateFee: null,
        busStopPickupTimes: [],
        returnBusStops: [],
        returnPickupTimes: [],
        metadata: null,
        transportationProfiles: []
      },
      metadataText: '',
      apiInProgress: false,
      deleteInProgress: false,
      unarchiveInProgress: false,
      breadcrumbs: [],
      eventDateMenu: false,
      eventDateLocal: null,
      metrics: null,
      metricsLoading: false,
      manageVehiclesDialog: false,
      manageBusStopTimesDialog: false,
      manageReturnPickupTimesDialog: false,
      manageReturnBusStopDialog: false,
      editingPickupIndex: -1,
      editingReturnPickupIndex: -1,
      editingReturnBusStopIndex: -1
    }
  },
  computed: {
    eventId () {
      const p = this.$route && this.$route.params
      return (p && p.eventId) ? String(p.eventId) : '0'
    },
    isNewEvent () {
      return !this.eventId || this.eventId === '0'
    },
    isAdmin () {
      return isAuthorized(this.signedInUser, 'admin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.signedInUser)
    },
    isEditable () {
      return this.currentEvent &&
      !this.currentEvent.dateArchived &&
      this.isAdmin &&
      !this.apiInProgress &&
      !this.deleteInProgress &&
      !this.unarchiveInProgress &&
      !this.metricsLoading
    },
    computedEventDateFormatted () {
      return this.currentEvent && this.currentEvent.eventDate
        ? formatDate(this.currentEvent.eventDate)
        : ''
    },
    transportationProfileIds () {
      if (!this.currentEvent || !Array.isArray(this.currentEvent.transportationProfiles)) {
        return []
      }

      return this.currentEvent.transportationProfiles.map((p) => p.id)
    }
  },
  async mounted () {
    this.breadcrumbs = initBreadcrumbs.call(this)

    if (!this.isNewEvent) {
      await this.loadEvent()
      this.refreshMetrics()
    }
  },
  methods: {
    async loadEvent () {
      this.apiInProgress = true

      try {
        const result = await getEvent({ id: this.eventId })
        this.setEvent(result)
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    setEvent (result) {
      this.currentEvent = result || this.currentEvent

      this.currentEvent.returnBusStops = this.currentEvent.returnBusStopId ? [{ busStopId: this.currentEvent.returnBusStopId, label: this.currentEvent.returnBusStopLabel }] : []

      if (this.currentEvent?.eventDate) {
        this.eventDateLocal = moment(this.currentEvent.eventDate).format('YYYY-MM-DD')
      }

      if (this.currentEvent?.transportationProfileEventProfiles?.length) {
        this.currentEvent.transportationProfiles = this.currentEvent.transportationProfileEventProfiles.map((t) => t.transportationProfile)
      }

      if (this.currentEvent?.metadata) {
        try {
          this.metadataText = JSON.stringify(this.currentEvent.metadata, null, 2)
        } catch (e) {
          this.metadataText = ''
        }
      }
    },
    onChangeEventDate (value) {
      this.eventDateLocal = value

      if (value) {
        const m = moment(value, 'YYYY-MM-DD')
        this.currentEvent.eventDate = m.toDate()
      } else {
        this.currentEvent.eventDate = null
      }
    },
    normalizePayload () {
      return {
        name: this.currentEvent.name,
        eventDate: this.currentEvent.eventDate,
        flatRateFee: this.currentEvent.flatRateFee,
        returnFlatRateFee: this.currentEvent.returnFlatRateFee,
        returnBusStopId: this.currentEvent.returnBusStops?.[0]?.busStopId,
        returnBusStopLabel: this.currentEvent.returnBusStops?.[0]?.label || undefined,
        busStopPickupTimes: this.currentEvent.busStopPickupTimes || [],
        returnPickupTimes: this.currentEvent.returnPickupTimes || [],
        transportationProfileIds: this.currentEvent.transportationProfiles?.map((t) => t.id)
      }
    },
    async onSave () {
      if (!this.isAdmin) {
        return
      }

      try {
        const payload = this.normalizePayload()

        if (!payload.name) {
          alert('Please provide an event name')
          return
        }

        if (!payload.eventDate) {
          alert('Please provide an event date')
          return
        }

        if (!payload.transportationProfileIds?.length) {
          alert('Please select at least one transportation profile')
          return
        }

        this.apiInProgress = true
        let saved

        if (this.isNewEvent) {
          saved = await createEvent(payload)

          addAlert({
            message: 'Event created',
            type: 'success',
            transient: true
          })

          this.setEvent(saved)
          this.$router.push({ name: 'routes-events' })
        } else {
          saved = await updateEvent({ id: this.currentEvent.id, ...payload })

          addAlert({
            message: 'Event updated',
            type: 'success',
            transient: true
          })

          this.setEvent(saved)
        }
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async onUnarchive () {
      if (!this.isAdmin || this.isNewEvent) {
        return
      }

      if (!confirm('Confirm archiving this event?')) {
        return
      }

      this.unarchiveInProgress = true
      try {
        await unarchiveEvent({ id: this.currentEvent.id })

        addAlert({
          message: 'Event unarchived',
          type: 'success',
          transient: true
        })

        this.$router.push({ name: 'routes-events' })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.unarchiveInProgress = false
      }
    },
    async onArchive () {
      if (!this.isAdmin || this.isNewEvent) {
        return
      }

      if (!confirm('Confirm archiving this event?')) {
        return
      }

      this.deleteInProgress = true
      try {
        await archiveEvent({ id: this.currentEvent.id })

        addAlert({
          message: 'Event deleted',
          type: 'success',
          transient: true
        })

        this.$router.push({ name: 'routes-events' })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.deleteInProgress = false
      }
    },
    async refreshMetrics () {
      if (this.isNewEvent) {
        return
      }

      this.metricsLoading = true

      try {
        this.metrics = await getEventCapacityStatus({ id: this.currentEvent.id })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.metricsLoading = false
      }
    },
    async refreshTransportationProfiles () {
      if (!Array.isArray(this.currentEvent.transportationProfiles) || !this.currentEvent.transportationProfiles.length) {
        return []
      }

      this.metricsLoading = true

      try {
        this.currentEvent.transportationProfiles = await findTransportationProfiles({ where: { id: { inq: this.transportationProfileIds } } })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.metricsLoading = false
      }
    },
    openNewPickupTimeDialog () {
      if (!this.isAdmin) {
        return
      }

      this.editingPickupIndex = -1
      this.manageBusStopTimesDialog = true
    },
    openNewReturnPickupTimeDialog () {
      if (!this.isAdmin) {
        return
      }

      this.editingReturnPickupIndex = -1
      this.manageReturnPickupTimesDialog = true
    },
    openNewReturnBusStopDialog () {
      if (!this.isAdmin) {
        return
      }

      if (this.currentEvent.returnBusStops?.length) {
        return
      }

      this.editingReturnBusStopIndex = -1
      this.manageReturnBusStopDialog = true
    },
    openEditPickupTimeDialog (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.busStopPickupTimes)) {
        return
      }

      if (index < 0 || index >= this.currentEvent.busStopPickupTimes.length) {
        return
      }

      this.editingPickupIndex = index
      this.manageBusStopTimesDialog = true
    },
    openEditReturnPickupTimeDialog (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.returnPickupTimes)) {
        return
      }

      if (index < 0 || index >= this.currentEvent.returnPickupTimes.length) {
        return
      }

      this.editingReturnPickupIndex = index
      this.manageReturnPickupTimesDialog = true
    },
    openEditReturnBusStopDialog (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.returnBusStops)) {
        return
      }

      if (index < 0) {
        return
      }

      this.editingReturnBusStopIndex = index
      this.manageReturnBusStopDialog = true
    },
    onPickupTimesChange (items) {
      const list = Array.isArray(items) ? items : []
      this.currentEvent.busStopPickupTimes = list
      this.editingPickupIndex = -1
      this.manageBusStopTimesDialog = false
    },
    onReturnPickupTimesChange (items) {
      const list = Array.isArray(items) ? items : []
      this.currentEvent.returnPickupTimes = list
      this.editingReturnPickupIndex = -1
      this.manageReturnPickupTimesDialog = false
    },
    onReturnBusStopChange (items) {
      const list = Array.isArray(items) ? items : []
      this.currentEvent.returnBusStops = list
      this.editingReturnBusStopIndex = -1
      this.manageReturnBusStopDialog = false
    },
    onDeletePickupTime (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.busStopPickupTimes)) {
        return
      }

      if (index < 0 || index >= this.currentEvent.busStopPickupTimes.length) {
        return
      }

      if (!confirm('Are you sure you want to delete these pickup time(s)?')) {
        return
      }

      const list = this.currentEvent.busStopPickupTimes.slice()
      const item = list[index]

      // Grouping rules mirror the UI: group by busStopId when present; otherwise group by label.
      if (item && item.busStopId) {
        this.currentEvent.busStopPickupTimes = list.filter(function (x) {
          return !(x && x.busStopId === item.busStopId)
        })
        return
      }

      if (item && item.label) {
        this.currentEvent.busStopPickupTimes = list.filter(function (x) {
          return !(x && !x.busStopId && x.label === item.label)
        })
        return
      }

      list.splice(index, 1)
      this.currentEvent.busStopPickupTimes = list
    },
    onDeleteReturnPickupTime (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.returnPickupTimes)) {
        return
      }

      if (index < 0 || index >= this.currentEvent.returnPickupTimes.length) {
        return
      }

      if (!confirm('Are you sure you want to delete this pickup time?')) {
        return
      }

      const list = this.currentEvent.returnPickupTimes.slice()
      list.splice(index, 1)
      this.currentEvent.returnPickupTimes = list
    },
    onDeleteReturnBusStop (index) {
      if (!this.isAdmin) {
        return
      }

      if (!Array.isArray(this.currentEvent.returnBusStops)) {
        return
      }

      if (index < 0 || index >= this.currentEvent.returnBusStops.length) {
        return
      }

      if (!confirm('Are you sure you want to delete this bus stop?')) {
        return
      }

      const list = this.currentEvent.returnBusStops.slice()
      list.splice(index, 1)
      this.currentEvent.returnBusStops = list
    },
    onManageVehiclesSubmit (payload) {
      this.manageVehiclesDialog = false

      if (!this.isAdmin) {
        return
      }

      this.currentEvent.transportationProfiles = payload && Array.isArray(payload.profiles) ? payload.profiles.slice() : []
    },
    onManageVehiclesCancel () {
      this.manageVehiclesDialog = false
    }
  }
}
